import React, { useState } from 'react';
import { Search, Filter, MoreVertical, Eye, Edit, Trash2, Download, CheckCircle, XCircle, Clock, ChevronLeft, ChevronRight, Calendar, User, Package, DollarSign, CreditCard } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAdminOrders, updateAdminOrderStatus } from '../../redux/adminOrdersSlice';

const Orders = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const dispatch = useDispatch();
  const { orders, loading, error } = useSelector((state) => state.adminOrders);

  React.useEffect(() => {
    dispatch(fetchAdminOrders());
  }, [dispatch]);

  const getStatusConfig = (status) => {
    switch (status) {
      case 'Delivered': return { label: 'Delivered', icon: CheckCircle, color: '#10b981', bg: '#d1fae5' };
      case 'Processing': return { label: 'Processing', icon: Clock, color: '#f59e0b', bg: '#fed7aa' };
      case 'Shipped': return { label: 'Shipped', icon: Package, color: '#3b82f6', bg: '#dbeafe' };
      case 'Cancelled': return { label: 'Cancelled', icon: XCircle, color: '#ef4444', bg: '#fee2e2' };
      default: return { label: status, icon: Clock, color: '#64748b', bg: '#f1f5f9' };
    }
  };

  const getPaymentConfig = (payment) => {
    switch (payment) {
      case 'Paid': return { label: 'Paid', color: '#10b981', bg: '#d1fae5' };
      case 'Pending': return { label: 'Pending', color: '#f59e0b', bg: '#fed7aa' };
      case 'Refunded': return { label: 'Refunded', color: '#ef4444', bg: '#fee2e2' };
      default: return { label: payment, color: '#64748b', bg: '#f1f5f9' };
    }
  };

  const filteredOrders = (orders || []).filter(order => {
    const customerName = order.buyer?.username || 'Unknown';
    const matchesSearch = order._id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          customerName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || order.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
  const paginatedOrders = filteredOrders.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const stats = {
    total: orders?.length || 0,
    delivered: orders?.filter(o => o.orderStatus === 'Delivered').length || 0,
    processing: orders?.filter(o => o.orderStatus === 'Processing').length || 0,
    pending: orders?.filter(o => o.orderStatus === 'Shipped').length || 0,
    revenue: orders?.reduce((sum, o) => sum + o.totalAmount, 0) || 0
  };

  if (loading) return <div className="p-4">Loading orders...</div>;
  if (error) return <div className="p-4 text-danger">{error}</div>;

  const handleUpdateStatus = (id, newStatus) => {
    if (window.confirm(`Update order status to ${newStatus}?`)) {
      dispatch(updateAdminOrderStatus({ id, status: newStatus }));
    }
  };

  return (
    <div className="admin-orders-modern">
      {/* Header */}
      <div className="orders-header">
        <div>
          <h1 className="page-title">Orders Management</h1>
          <p className="page-subtitle">View and manage all customer orders</p>
        </div>
        <button className="btn-export">
          <Download size={16} />
          Export CSV
        </button>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon blue">
            <Package size={20} />
          </div>
          <div>
            <h3>{stats.total}</h3>
            <span>Total Orders</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon green">
            <CheckCircle size={20} />
          </div>
          <div>
            <h3>{stats.delivered}</h3>
            <span>Delivered</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon orange">
            <Clock size={20} />
          </div>
          <div>
            <h3>{stats.processing}</h3>
            <span>Processing</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon purple">
            <DollarSign size={20} />
          </div>
          <div>
            <h3>₹{stats.revenue.toLocaleString()}</h3>
            <span>Revenue</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-box">
          <Search size={18} />
          <input 
            type="text" 
            placeholder="Search by order ID or customer..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="status-filters">
          <button className={`filter-btn ${filterStatus === 'all' ? 'active' : ''}`} onClick={() => setFilterStatus('all')}>All</button>
          <button className={`filter-btn ${filterStatus === 'Delivered' ? 'active' : ''}`} onClick={() => setFilterStatus('Delivered')}>Delivered</button>
          <button className={`filter-btn ${filterStatus === 'Processing' ? 'active' : ''}`} onClick={() => setFilterStatus('Processing')}>Processing</button>
          <button className={`filter-btn ${filterStatus === 'Shipped' ? 'active' : ''}`} onClick={() => setFilterStatus('Shipped')}>Shipped</button>
          <button className={`filter-btn ${filterStatus === 'Cancelled' ? 'active' : ''}`} onClick={() => setFilterStatus('Cancelled')}>Cancelled</button>
        </div>
      </div>

      {/* Orders Table */}
      <div className="table-container">
        <div className="table-responsive">
          <table className="orders-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Customer</th>
                <th>Date</th>
                <th>Items</th>
                <th>Total</th>
                <th>Payment</th>
                <th>Status</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedOrders.map((order) => {
                const statusConfig = getStatusConfig(order.status);
                const StatusIcon = statusConfig.icon;
                const paymentConfig = getPaymentConfig(order.paymentStatus || 'Paid');
                
                return (
                  <tr key={order._id}>
                    <td>
                      <span className="order-id">#{order._id.substring(order._id.length - 6).toUpperCase()}</span>
                    </td>
                    <td>
                      <div className="customer-cell">
                        <div className="customer-avatar">
                          {order.buyer?.username ? order.buyer.username.charAt(0).toUpperCase() : 'U'}
                        </div>
                        <span>{order.buyer?.username || 'Unknown'}</span>
                      </div>
                    </td>
                    <td className="date-cell">{new Date(order.createdAt).toLocaleDateString()}</td>
                    <td>{order.orderItems?.length || 0} items</td>
                    <td className="amount-cell">₹{order.totalAmount?.toLocaleString()}</td>
                    <td>
                      <span className="payment-badge" style={{ backgroundColor: paymentConfig.bg, color: paymentConfig.color }}>
                        {paymentConfig.label}
                      </span>
                    </td>
                    <td>
                      <button className="status-badge" style={{ backgroundColor: statusConfig.bg, color: statusConfig.color, border: 'none', cursor: 'pointer' }}
                            title="Click to update status"
                            onClick={() => {
                              const nextStatus = order.status === 'Processing' ? 'Shipped' : order.status === 'Shipped' ? 'Delivered' : 'Processing';
                              handleUpdateStatus(order._id, nextStatus);
                            }}>
                        <StatusIcon size={12} />
                        {statusConfig.label}
                      </button>
                    </td>
                    <td className="text-right">
                      <div className="action-buttons">
                        <button className="action-btn view" title="View Details">
                          <Eye size={16} />
                        </button>
                        <button className="action-btn delete" title="Delete">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {paginatedOrders.length === 0 && (
                <tr>
                  <td colSpan="8" className="text-center py-5 text-muted">No orders found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="pagination-wrapper">
          <div className="pagination-info">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredOrders.length)} of {filteredOrders.length} entries
          </div>
          <div className="pagination-buttons">
            <button 
              className={`page-btn ${currentPage === 1 ? 'disabled' : ''}`} 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft size={16} /> Previous
            </button>
            {[...Array(totalPages)].map((_, i) => (
              <button 
                key={i}
                className={`page-number ${currentPage === i + 1 ? 'active' : ''}`}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
            <button 
              className={`page-btn ${currentPage === totalPages ? 'disabled' : ''}`} 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      
    </div>
  );
};

export default Orders;